/**
 * Pure fetch helper for AuditEvent pages — returns data so callers can
 * guard against stale responses with a generation token.
 */
import type { MedplumClient } from '@medplum/core';
import type { AuditEvent, Bundle } from '@medplum/fhirtypes';
import { type AuditEventSearchFilters, toAuditEventSearchQuery } from './buildAuditEventSearchParams';
import { mapAuditEventToRow, type AuditLogRow } from './mapAuditEventToRow';

export interface AuditEventPageResult {
  rows: AuditLogRow[];
  /**
   * Exact Bundle.total when present. Otherwise the known row span, plus one
   * when Bundle.link includes relation=next so the footer can advance without
   * inventing a full phantom page of pageSize rows.
   */
  total: number;
}

function bundleHasNextLink(bundle: Bundle): boolean {
  return bundle.link?.some((l) => l.relation === 'next') === true;
}

export async function fetchAuditEventPage(
  medplum: Pick<MedplumClient, 'search'>,
  filters: AuditEventSearchFilters,
): Promise<AuditEventPageResult> {
  const query = toAuditEventSearchQuery(filters);
  const bundle = (await medplum.search('AuditEvent', query)) as Bundle<AuditEvent>;
  const resources =
    bundle.entry
      ?.map((e) => e.resource)
      .filter((r): r is AuditEvent => r?.resourceType === 'AuditEvent') ?? [];
  const mapped = resources.map(mapAuditEventToRow);
  const pageOffset = filters.offset ?? 0;
  // Prefer Bundle.total from `_total=accurate`. Without it: never add a full
  // pageSize (phantom empty last page); only +1 when a next link proves more exist.
  const total =
    typeof bundle.total === 'number'
      ? bundle.total
      : pageOffset + mapped.length + (bundleHasNextLink(bundle) ? 1 : 0);
  return {
    rows: mapped,
    total,
  };
}
